import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CalendarClock } from "lucide-react";
import { GlassCard, StatusBadge } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { formatGBP } from "../../components/common/StockBadge";

import api from "../../lib/api";

const STATUS_LABEL = {
  RESERVATION_RECEIVED: "Received", UNDER_REVIEW: "Under Review", CONFIRMED: "Confirmed",
  READY_FOR_COLLECTION: "Ready for Collection",
};
const daysLabel = (n) => n == null ? "" : n < 0 ? "Collection date passed" : n === 0 ? "Collection today" : n === 1 ? "Collection tomorrow" : `${n} days`;
const dayCls = (n) => n == null ? "text-white/50" : n < 0 ? "text-firered" : n <= 1 ? "text-gold" : "text-white/70";

export default function AdminUpcomingCollections() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/reservations/upcoming/collections", { params: { page, page_size: 25 } })
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [page]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-upcoming-collections">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Upcoming Collections</h1>
        <p className="mt-1 text-muted-foreground text-sm">Active reservations ordered by nearest preferred collection date.</p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <GlassCard className="p-12 flex flex-col items-center text-center" hover={false} data-testid="upcoming-empty">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-gold/10 border border-gold/20"><CalendarClock className="h-7 w-7 text-gold" /></div>
          <p className="mt-5 font-playfair text-xl text-white">No upcoming collections</p>
          <p className="mt-2 text-sm text-muted-foreground">Active reservations awaiting collection will appear here.</p>
        </GlassCard>
      ) : (
        <>
          <div className="hidden lg:block overflow-hidden rounded-xl border border-white/10" data-testid="upcoming-table">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-3">Collection</th><th className="text-left p-3">Days</th>
                  <th className="text-left p-3">Number</th><th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Mobile</th><th className="text-center p-3">Items</th>
                  <th className="text-right p-3">Total</th><th className="text-left p-3">Status</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.reservation_number} data-testid={`upcoming-row-${r.reservation_number}`} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white/80">{r.collection_date}</td>
                    <td className={`p-3 font-medium ${dayCls(r.days_until_collection)}`}>{daysLabel(r.days_until_collection)}</td>
                    <td className="p-3 text-gold font-medium">{r.reservation_number}</td>
                    <td className="p-3 text-white/80">{r.customer_name}</td>
                    <td className="p-3 text-white/60">{r.customer_phone}</td>
                    <td className="p-3 text-center text-white/70">{r.item_count}</td>
                    <td className="p-3 text-right text-white">{formatGBP(r.estimated_total)}</td>
                    <td className="p-3"><StatusBadge status={STATUS_LABEL[r.status] || r.status} /></td>
                    <td className="p-3 text-right"><Button size="sm" variant="outline" className="border-white/20 text-white" onClick={() => navigate(`/admin/reservations/${r.reservation_number}`)} data-testid={`upcoming-view-${r.reservation_number}`}>View</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="lg:hidden space-y-3">
            {items.map((r) => (
              <div key={r.reservation_number} onClick={() => navigate(`/admin/reservations/${r.reservation_number}`)} data-testid={`upcoming-card-${r.reservation_number}`} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex justify-between items-start gap-2"><span className="text-gold font-semibold">{r.reservation_number}</span><StatusBadge status={STATUS_LABEL[r.status] || r.status} /></div>
                <p className="mt-1 text-sm text-white/80">{r.customer_name} · {r.customer_phone}</p>
                <p className="mt-1 text-xs text-white/50">Collection {r.collection_date} · <span className={dayCls(r.days_until_collection)}>{daysLabel(r.days_until_collection)}</span> · {r.item_count} items · {formatGBP(r.estimated_total)}</p>
              </div>
            ))}
          </div>

          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3" data-testid="upcoming-pagination">
              <Button variant="outline" className="border-white/20 text-white" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages} · {data.total} total</span>
              <Button variant="outline" className="border-white/20 text-white" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)}>Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
